import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard } from "@/components/admin/StatCard";
import { DollarSign, CreditCard, Repeat, FileText } from "lucide-react";

const money = (n, c = "usd") => new Intl.NumberFormat("en-US", { style: "currency", currency: (c || "usd").toUpperCase() }).format(n || 0);

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <p className="text-muted-foreground">Loading revenue…</p>;

  return (
    <div data-testid="admin-revenue">
      <h1 className="font-serif text-4xl tracking-tight mb-1">Revenue</h1>
      <p className="text-muted-foreground mb-8">Recurring revenue and payment activity (Stripe test data).</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="rev-mrr" label="MRR" value={money(data.mrr)} accent sub={`ARR ${money(data.arr)}`} />
        <StatCard testid="rev-total" label="Total collected" value={money(data.total_revenue)} sub={`${data.paid_count} payments`} />
        <StatCard testid="rev-subs" label="Subscription rev." value={money(data.subscription_revenue)} sub={`${data.active_monthly} monthly · ${data.active_annual} annual`} />
        <StatCard testid="rev-forms" label="Pay-per-form rev." value={money(data.form_revenue)} sub="$3.95 unlocks" />
      </div>

      {data.stripe_gross != null && (
        <div className="grid-card p-5 mb-6 flex items-center gap-3" data-testid="rev-stripe">
          <DollarSign size={18} className="text-accent" />
          <span className="text-sm">Stripe gross (balance transactions): <strong>{money(data.stripe_gross)}</strong></span>
        </div>
      )}

      <div className="grid-card p-6" data-testid="recent-payments">
        <div className="flex items-center gap-2 mb-6">
          <CreditCard size={16} className="text-accent" />
          <h2 className="font-serif text-xl">Recent payments</h2>
        </div>
        {data.recent_payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No completed payments yet. Subscriptions and single-form unlocks will appear here.</p>
        ) : (
          <div className="divide-y divide-border">
            {data.recent_payments.map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                {p.type === "Subscription" ? <Repeat size={15} className="text-muted-foreground" /> : <FileText size={15} className="text-muted-foreground" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{p.type}{p.form_number ? ` · ${p.form_number}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{p.date ? new Date(p.date).toLocaleString() : "—"}</p>
                </div>
                <span className="text-sm font-semibold tabular-nums">{money(p.amount, p.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
